// Login card

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Card from './Card';
import Input from './Input';
import isEmail from 'validator/lib/isEmail';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, authSendPasswordResetEmail } from '../utils/firebase';
import Spinner from './Spinner';
import { useModal } from './ModalProvider';
import { useFirebase } from './FirebaseContextProvider';
import { UsersRoles } from '../utils/enums';
import './LoginCard.scss';

const authMsgs = {
  'auth/invalid-credential': 'Invalid email or password',
};

export const getBaseRoute = (user) => {
  let baseRoute = `/`;

  switch (user?.role) {
    case UsersRoles.ADMIN:
      baseRoute = '/admin';
      break;
    case UsersRoles.COACH:
      baseRoute = '/coaches/' + user.id;
      break;
    case UsersRoles.REFEREE:
      baseRoute = '/referees/' + user.id;
      break;
    case UsersRoles.REFEREE_ASSIGNER:
      baseRoute = '/referee-assigners/' + user.id;
      break;
    case UsersRoles.PLAYER:
      baseRoute = '/players/' + user.id;
      break;
    case UsersRoles.FIELDMANAGER:
      baseRoute = '/field-managers/' + user.id;
      break;
    case UsersRoles.PARENT:
      baseRoute = '/parents/' + user.id;
      break;
    case UsersRoles.NONE:
      baseRoute = '/none/' + user.id;
      break;
    default:
      break;
  }

  return baseRoute;
};

export default function LoginCard() {
  const [spinner, setSpinner] = useState(false);
  const { isUserResolved } = useFirebase();
  const { openModal } = useModal();
  const location = useLocation();

  const initialValues = {
    email: location.state?.email ?? '',
    password: '',
  };

  // Show spinner until we know who the user is
  useEffect(() => {
    setSpinner(!isUserResolved);
  }, [isUserResolved]);

  const validationSchema = new Yup.ObjectSchema({
    email: Yup.string()
      .test('is-valid-email', 'Invalid email', (value) => isEmail(value))
      .required('Required'),
    password: Yup.string().required('Required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSpinner(true);
        await signInWithEmailAndPassword(auth, values.email, values.password);
      } catch (error) {
        toast.error(authMsgs[error.code] ?? error.message);
      } finally {
        setSpinner(false);
      }
    },
  });

  async function handleForgotPassword(e) {
    e.preventDefault();

    // mark field touched & validate
    formik.setFieldTouched('email', true, true);
    try {
      await validationSchema.validateAt('email', {
        email: formik.values.email,
      });
    } catch (validationError) {
      formik.setFieldError('email', validationError.message);
      return;
    }

    // ask user to confirm
    openModal({
      header: 'Reset password',
      body: (
        <p className='font-16'>
          Do you want to reset the password for email{' '}
          <span className='font-medium'>{formik.values.email}</span>?
        </p>
      ),
      buttons: [
        { text: 'Cancel', className: 'bw' },
        {
          text: 'Ok',
          className: 'primary',
          onClick: async () => {
            setSpinner(true);
            try {
              await authSendPasswordResetEmail({ email: formik.values.email });
              toast.success(
                `Email with a reset link was successfully sent, please check the mailbox ${formik.values.email}`,
              );
            } catch (error) {
              toast.error(error.message);
            } finally {
              setSpinner(false);
            }
          },
        },
      ],
    });
  }

  return (
    <Card header='Login' className='login-card'>
      <form className='login-form' onSubmit={formik.handleSubmit}>
        <p className='font-16'>
          Please sign in with your email & password, or{' '}
          <Link
            to='/auth'
            state={{ email: formik.values.email }}
            className='font-16'
          >
            get an auth link
          </Link>
        </p>
        <Input
          name='email'
          placeholder='E-mail'
          {...formik.getFieldProps('email')}
          error={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : null
          }
        />
        <Input
          name='password'
          placeholder='Password'
          type='password'
          {...formik.getFieldProps('password')}
          error={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : null
          }
        />
        <Link
          to='#'
          className='font-medium reset-password'
          onClick={handleForgotPassword}
        >
          Forgot password
        </Link>
        <button className='signin-btn' type='submit'>
          Sign in
        </button>
      </form>
      <Spinner show={spinner} />
    </Card>
  );
}
